import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Dropdown} from '@/components/atoms';
import ProductSelectionModal from './ProductSelectionModal';
import styles from '../RewardSetup.module.scss';
import PointsCondition from './PointsCondition';
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants';
import NonInteractiveDropdownTrigger from './NonInteractiveDropdownTrigger';

/**
 * ProductOrPepVoucherCondition component displays the product or PEP voucher reward condition UI
 * Handles product selection via modal triggered by dropdown option selection
 * For PRODUCT type: uses handleProductsChange and selectedProducts field
 * For PEP_VOUCHER_PRODUCT type: uses handlePepVouchersChange and selectedPepVouchersProducts field
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing selectedProducts or selectedPepVouchersProducts
 * @param {Object} props.validation - Validation state object
 * @param {boolean} props.pepVouchersOrProductsLoading - Loading state for products or PEP vouchers
 * @param {Function} props.handleProductsChange - Handler for products selection changes (PRODUCT type)
 * @param {Function} props.handlePepVouchersChange - Handler for PEP vouchers selection changes (PEP_VOUCHER_PRODUCT type)
 * @returns {JSX.Element} The rendered ProductOrPepVoucherCondition component
 */
const ProductOrPepVoucherCondition = ({
  formData,
  validation,
  pepVouchersOrProductsLoading,
  handleProductsChange,
  handlePepVouchersChange,
  handlePointsChange,
  pointsError
}) => {
  const [showProductModal, setShowProductModal] = useState(false);

  // Determine which handler and field to use based on reward type
  const isProductType = formData.rewardType === REWARD_TYPES.PRODUCT;
  const handleChange = isProductType
    ? handleProductsChange
    : handlePepVouchersChange;
  const selectedProducts = isProductType
    ? formData.selectedProducts || []
    : formData.selectedPepVouchersProducts || [];
  const validationKey = isProductType
    ? 'selectedProductsValid'
    : 'selectedPepVouchersProductsValid';

  const handleOpenProductModal = () => {
    if (!pepVouchersOrProductsLoading) setShowProductModal(true);
  };

  // Handle closing the product selection modal
  const handleCloseProductModal = () => {
    setShowProductModal(false);
  };

  // Handle product selection from modal
  const handleProductSelection = (selectedProductsData) => {
    // Convert selected products to the format expected by the parent component
    // The parent expects an array of objects with value and label properties
    const selectedProductValues = selectedProductsData.map((product) => ({
      value: product.productId,
      label: product.productName
    }));

    if (handleChange) {
      handleChange(selectedProductValues);
    }
  };

  // Create display options for dropdown based on selected products
  // Uses selectedProducts for PRODUCT type, selectedPepVouchersProducts for PEP_VOUCHER_PRODUCT type
  const selectedProductOptions = selectedProducts;
  // Create dynamic placeholder based on selected products
  const getPlaceholder = () => {
    if (pepVouchersOrProductsLoading) return 'Loading...';

    if (selectedProductOptions.length === 0) return 'Select Products';

    if (selectedProductOptions.length === 1) {
      return selectedProductOptions[0].label;
    }
  };

  return (
    <div className='flex flex-wrap gap-[13px]'>
      <div
        className={`${styles.rewardSetup__productsDropdownWrapper} text-left`}>
        <NonInteractiveDropdownTrigger
          id='product-or-pep-products-dropdown-trigger'
          testId='product-or-pep-products-dropdown-trigger'
          ariaLabel='Open product selection'
          onOpen={handleOpenProductModal}>
          <Dropdown
            className={`${styles.rewardSetup__products_dropdown} text-sm placeholder:text-sm`}
            value={selectedProductOptions}
            isError={validation[validationKey]}
            errorMessage={
              validation[validationKey]
                ? 'At least one product must be selected'
                : ''
            }
            isDark={true}
            childList={[]}
            placeholder={getPlaceholder()}
            isJsonWrap={false}
            selection='multiple'
            hideMenuListOnSelection={true}
            isClearable={false}
            preventTyping={true}
            updateValue={() => {}}
            style={{width: '260px'}}
          />
        </NonInteractiveDropdownTrigger>
      </div>

      <PointsCondition
        formData={formData}
        handlePointsChange={handlePointsChange}
        pointsError={pointsError}
      />
      {/* Product Selection Modal */}
      <ProductSelectionModal
        showModal={showProductModal}
        onCloseModal={handleCloseProductModal}
        onSelectProducts={handleProductSelection}
        selectedProducts={
          selectedProducts?.map((p) => ({
            productId: p.value,
            productName: p.displayText || p.label
          })) || []
        }
        rewardType={formData.rewardType}
      />
    </div>
  );
};

ProductOrPepVoucherCondition.propTypes = {
  formData: PropTypes.shape({
    rewardType: PropTypes.string,
    selectedProducts: PropTypes.array,
    selectedPepVouchersProducts: PropTypes.array,
    points: PropTypes.string
  }).isRequired,
  validation: PropTypes.shape({
    selectedProductsValid: PropTypes.bool,
    selectedPepVouchersProductsValid: PropTypes.bool
  }).isRequired,
  pepVouchersOrProductsLoading: PropTypes.bool.isRequired,
  handleProductsChange: PropTypes.func,
  handlePepVouchersChange: PropTypes.func,
  handlePointsChange: PropTypes.func,
  pointsError: PropTypes.string
};

export default ProductOrPepVoucherCondition;
